import { Plus, Minus } from "lucide-react";
import FitNumber from "../../common/FitNumber";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { ThinDropdownWithSearch, ThinTextField, } from "../../../components/common/GeneralComponent";
import { TrendingUp, Users, CheckCircle, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { fetchData } from "../../../utils/functions";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import DashboardSearchFilters from "../../common/DashboardSearchFilters";
import NoData, { hasData } from "../../common/NoData";
import SharedDonut from "../../common/DonutChart";
import DashboardBarChart from "../../common/DashboardBarChart";
import {
  BRAND,
  NEUTRAL,
  STATUS_COLORS,
  getPaletteColor,
} from "../../../utils/chartTheme";
import { formatCount } from "../../../utils/numberFormat";


const DonutChart = ({ data, centerValue, centerLabel }) => {
  const allWaterSourceData = [
    { key: "Tap Water", name: "Tap Water", value: 0, color: getPaletteColor(0) },
    { key: "Hand Pump", name: "Hand Pump", value: 0, color: getPaletteColor(1) },
    { key: "Protected Dug Well", name: "Protected Dug Well", value: 0, color: getPaletteColor(2), },
    { key: "Unprotected Dug Well", name: "Unprotected Dug Well", value: 0, color: getPaletteColor(3), },
    { key: "Rain Water", name: "Rain Water", value: 0, color: getPaletteColor(4) },
    { key: "Surface Water", name: "Surface Water", value: 0, color: getPaletteColor(5) },
  ];
  // Merge incoming API data
  if (data && data.length > 0) {
    data.forEach((item) => {
      const idx = allWaterSourceData.findIndex((d) => d.name === item.name);
      if (idx !== -1) allWaterSourceData[idx].value = item.value;
    });
  }
  const total = allWaterSourceData.reduce((sum, item) => sum + item.value, 0);
  const radius = 40;
  const strokeWidth = 8;
  const center = radius + strokeWidth;
  const circumference = 2 * Math.PI * radius;
  let cumulativePercentage = 0;
  const leftSideData = allWaterSourceData.slice(0, 3);
  const rightSideData = allWaterSourceData.slice(3, 6);
  return (
    <div className="flex items-center justify-center gap-3">
      {/* Left Legend */}
      <div className="flex flex-col space-y-2">
        {leftSideData.map((item, index) => (
          <div key={index} className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} ></div>
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-gray-800"> {formatCount(item.value)} </span>
              <span className="text-gray-500 text-[10px] leading-tight"> {item.name} </span>
            </div>
          </div>
        ))}
      </div>
      {/* Donut */}
      <div className="relative flex-shrink-0" style={{ width: center * 2, height: center * 2 }} >
        <svg width={center * 2} height={center * 2} className="transform -rotate-90" >
          {/* Background Circle */}
          <circle cx={center} cy={center} r={radius} stroke={NEUTRAL.track} strokeWidth={strokeWidth} fill="transparent" />
          {/* Data Segments */}
          {allWaterSourceData
            .filter((item) => item.value > 0)
            .map((item, index) => {
              const percentage = total > 0 ? (item.value / total) * 100 : 0;
              const strokeDasharray = `${ (percentage / 100) * circumference } ${circumference}`;
              const strokeDashoffset = -( (cumulativePercentage / 100) * circumference );
              cumulativePercentage += percentage;
              return (
                <circle key={index} cx={center} cy={center} r={radius} stroke={item.color} strokeWidth={strokeWidth} fill="transparent" strokeDasharray={strokeDasharray} strokeDashoffset={strokeDashoffset} className="transition-all duration-300" />
              );
            })}
        </svg>
        {/* Center Text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-lg font-bold text-gray-800">{formatCount(centerValue)}</div>
          <div className="text-xs text-gray-600">{centerLabel}</div>
        </div>
      </div>
      {/* Right Legend */}
      <div className="flex flex-col space-y-2">
        {rightSideData.map((item, index) => (
          <div key={index} className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} ></div>
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-gray-800"> {formatCount(item.value)} </span>
              <span className="text-gray-500 text-[10px] leading-tight"> {item.name} </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FloodImpactPie = ({ data }) => {
  const chartData = useMemo(() => {
    return [
      { name: "No Damage", value: data?.noDamage || 0, color: getPaletteColor(0) },
      { name: "Repaired", value: data?.repair || 0, color: getPaletteColor(1) },
      { name: "Not Repaired", value: data?.noRepair || 0, color: getPaletteColor(2) },
      { name: "Minor Damage", value: data?.minorDamage || 0, color: getPaletteColor(3) },
    ].filter((item) => item.value > 0);
  }, [data]);

  if (!data || chartData.length === 0) {
    return <NoData />;
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);
  const pieData = chartData.map((item, idx) => ({
    id: idx,
    value: item.value,
    label: item.name,
    color: item.color,
  }));

  return (
    <div className="flex flex-col items-center justify-center w-full">
      <PieChart series={[
        {
          data: pieData,
          outerRadius: 115,
          innerRadius: 0,
          paddingAngle: 2,
          cornerRadius: 4,
          highlightScope: { fade: "global", highlight: "item" },
          arcLabel: (item) => {
            const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0";
            return `${percentage}%`;
          },
          arcLabelMinAngle: 35,
        }
      ]} width={350} height={280} margin={{ top: 10, right: 10, bottom: 40, left: 10 }} slotProps={{
        legend: {
          direction: "row",
          position: { vertical: "bottom", horizontal: "middle" },
          padding: -10,
          itemMarkWidth: 12,
          itemMarkHeight: 12,
          markGap: 5,
          itemGap: 15,
        },
      }} sx={{
        "& .MuiChartsLegend-series text": {
          fontSize: "12px !important",
          fontWeight: "600 !important",
        },
        "& .MuiPieArcLabel-root": {
          fontSize: "12px !important",
          fontWeight: "600 !important",
        },
      }} />
    </div>
  );
};

const WaterStatusBar = ({ data }) => {
  const chartData = useMemo(() => {
    return [
      { status: "Fully Functional", count: data?.fullyFunctional || 0, color: BRAND.teal, },
      { status: "Partly Functional", count: data?.partlyFunctional || 0, color: BRAND.gold, },
      { status: "Non-Functional", count: data?.nonFunctional || 0, color: getPaletteColor(4), },
    ];
  }, [data]);

  if (!data) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500"> No water status data available </div>
    );
  }

  const total = chartData.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-white flex flex-col h-full">
      <h3 className="text-lg font-bold text-center text-[#3A3A3A] uppercase mb-2"> Status of Water Source </h3>
      <div className="flex-1 flex flex-col justify-center px-6 space-y-2">
        {chartData.map((item, index) => {
          const percentage = total > 0 ? (item.count / total) * 100 : 0;
          return (
            <div key={index} className="flex items-center border-l pl-3 py-1.5 bg-gray-50" style={{ borderColor: item.color, borderLeftWidth: "3px" }} >
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-700"> {item.status} </div>
                <div className="text-xs text-gray-500"> {percentage.toFixed(1)}% of total </div>
              </div>
              <div className="text-lg font-bold" style={{ color: item.color }}> {item.count} </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WaterUsageHorizontalBar = ({ data }) => {
  const chartData = useMemo(() => {
    const bins = [
      { label: "0-2 hrs", value: data?.["0-2"] || 0, color: BRAND.teal },
      { label: "2-4 hrs", value: data?.["2-4"] || 0, color: BRAND.teal },
      { label: "4-6 hrs", value: data?.["4-6"] || 0, color: BRAND.teal },
      { label: "> 6 hrs", value: data?.[">6"] || 0, color: BRAND.teal },
    ];
    return bins;
  }, [data]);

  // Calculate max value for percentage calculation
  const maxValue = Math.max(...chartData.map((item) => item.value), 1);

  return (
    <div className="bg-white flex flex-col">
      <h3 className="text-lg font-bold text-center text-[#3A3A3A] uppercase mb-2">
        Water Used (Hours per Day)
      </h3>
      <div className="flex flex-col justify-center space-y-2.5 py-5 px-3">
        {chartData.map((item, index) => (
          <div key={index} className="flex items-center space-x-3">
            <div className="w-22 text-sm font-semibold text-gray-700">
              {item.label}
            </div>
            {item.value > 0 && (
              <div className="flex-1 bg-gray-200 rounded-full h-7 relative">
                <div
                  className="h-7 rounded-full flex items-center justify-end pr-2.5 text-white font-bold transition-all duration-500 text-sm"
                  style={{
                    width: `${(item.value / maxValue) * 100}%`,
                    backgroundColor: item.color,
                  }}
                >
                  {formatCount(item.value)}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Water Source Pie Chart Component
const WaterSourcePie = ({ data }) => {
  const chartData = useMemo(() => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    return data.map((item, idx) => ({
      id: idx,
      value: item.value,
      label: item.name,
      count: item.value,
      percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : "0",
      color: item.color,
    }));
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-500"> No water source data available </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <PieChart series={[
        {
          data: chartData,
          outerRadius: 90,
          paddingAngle: 3,
          cornerRadius: 4,
          highlightScope: { fade: "global", highlight: "item" },
        }
      ]} width={280} height={220} slotProps={{
        legend: { hidden: true, },
      }} />
      {/* Custom Legend */}
      <div className="w-full max-w-xs">
        <div className="space-y-2">
          {chartData.map((item, index) => (
            <div key={item.id} className="flex items-center justify-between text-sm p-2 hover:bg-gray-50 rounded-lg transition-colors duration-200 border border-gray-100" >
              <div className="flex items-center space-x-3 flex-1">
                <div className="w-4 h-4 rounded-full flex-shrink-0 shadow-sm" style={{ backgroundColor: item.color }} />
                <span className="text-gray-800 font-semibold leading-tight"> {item.label} </span>
              </div>
              <div className="flex items-center space-x-3 flex-shrink-0">
                <span className="text-gray-700 font-bold">{item.count}</span>
                <span className="text-gray-600 w-14 text-right font-medium bg-blue-50 px-2 py-1 rounded"> {item.percentage}% </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const formatNumber = (num) => {
  const numValue = typeof num === "string" ? parseInt(num) : num;
  if (numValue === undefined || numValue === null || isNaN(numValue)) return "0";
  if (numValue >= 1000000) {
    return `${(numValue / 1000000).toFixed(1)}M`;
  } else if (numValue >= 1000) {
    return `${(numValue / 1000).toFixed(1)}K`;
  } else {
    return numValue.toLocaleString();
  }
};

// OutcomesCard component to match the Total Allocated card design
const OutcomesCard = ({ title, value, icon, currency }) => (
  <div className="text-center">
    <div className={`rounded-2xl text-white text-xl w-9 h-9 mx-auto my-1 flex items-center justify-center ${ currency === "€" ? "bg-yellow-600" : "bg-teal-700" }`} >
      {icon}
    </div>
    <h5 className="text-xs m-0 text-gray-500 font-bold">{title}</h5>
    <h4 className="text-xl m-0 p-0 text-black font-bold text-balance">{value}</h4>
  </div>
);

const ActivityTypeCard = ({ title, count, variant = "default" }) => {
  const borderColors = {
    default: "#ededed",
    pending: STATUS_COLORS.planned,
    approved: BRAND.teal,
    rejected: getPaletteColor(2),
  };
  const borderColor = borderColors[variant] || borderColors.default;
  return (
    <div>
      <div className="bg-[#f7f7f7] text-white h-28 p-2.5 rounded-3xl border-2 hover:border-teal-300 transition-all duration-300 hover:shadow-md" style={{ borderColor }} >
        <h3 className="text-2xl text-center mt-4 mb-0 text-[#3A3A3A] p-0 font-bold text-balance"> <FitNumber value={count ?? 0} text={formatCount(count ?? 0)} /> </h3>
        <h6 className="text-xs text-center p-0 m-0 text-[#787878] font-semibold"> {title} </h6>
      </div>
    </div>
  );
};

const HouseholdsServedDonut = ({ waterServed, sanitationServed }) => {
  const totalServed = waterServed + sanitationServed;
  const waterPercentage = totalServed > 0 ? (waterServed / totalServed) * 100 : 0;
  const sanitationPercentage = totalServed > 0 ? (sanitationServed / totalServed) * 100 : 0;
  const radius = 40;
  const strokeWidth = 8;
  const center = radius + strokeWidth;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="flex items-center justify-center gap-3">
      {/* Left Legend - Water */}
      <div className="flex flex-col space-y-2">
        <div className="flex items-center gap-2 text-xs">
          {/* Added colored dot */}
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND.teal }}></div>
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-gray-800 text-sm">
              {formatCount(waterServed)}
            </span>
            <span className="text-gray-500 text-sm leading-tight">Water</span>
            <span className="text-sm font-semibold text-gray-800">
              {waterPercentage.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
      {/* Donut */}
      <div
        className="relative flex-shrink-0"
        style={{ width: center * 2, height: center * 2 }}
      >
        <svg
          width={center * 2}
          height={center * 2}
          className="transform -rotate-90"
        >
          {/* Background Circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={NEUTRAL.track}
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          {/* Water Segment */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={BRAND.teal}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${
              (waterPercentage / 100) * circumference
            } ${circumference}`}
            strokeDashoffset="0"
            className="transition-all duration-300"
          />
          {/* Sanitation Segment */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={BRAND.gold}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${
              (sanitationPercentage / 100) * circumference
            } ${circumference}`}
            strokeDashoffset={`-${(waterPercentage / 100) * circumference}`}
            className="transition-all duration-300"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-lg font-bold text-gray-800">{formatCount(totalServed)}</div>
          <div className="text-xs text-gray-600">Total</div>
        </div>
      </div>
      {/* Right Legend - Sanitation */}
      <div className="flex flex-col space-y-2">
        <div className="flex items-center gap-2 text-xs">
          {/* Added colored dot */}
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND.gold }}></div>
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-gray-800 text-sm">
              {formatCount(sanitationServed)}
            </span>
            <span className="text-gray-500 text-sm leading-tight">Sanitation</span>
            <span className="text-sm font-semibold text-gray-800">
              {sanitationPercentage.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const TotalYearlyExpenditureDistrictWiseChart = ({ expenditureData }) => {
  const { categories = [], data = [] } = expenditureData || {};
  if (!categories.length || !hasData(data)) {
    return <NoData />;
  }
  const totalExpenditure = data.reduce((sum, val) => sum + val, 0);
  const averageExpenditure = (totalExpenditure / data.length).toFixed(0);
  return (
    <div className="w-full h-full">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-[#E6F2F2] rounded-lg p-3 border border-[#7FBFC1]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[#0B5557] font-medium mb-1"> Total Expenditure </p>
              <p className="text-2xl font-bold text-[#137E81]"> ₨{formatNumber(totalExpenditure)} </p>
            </div>
            <div className="bg-[#137E81] rounded-full p-2">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-[#E6F2F2] rounded-lg p-3 border border-[#7FBFC1]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[#0B5557] font-medium mb-1"> Avg/District </p>
              <p className="text-2xl font-bold text-[#137E81]"> ₨{formatNumber(averageExpenditure)} </p>
            </div>
            <div className="bg-[#137E81] rounded-full p-2">
              <Users className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
      </div>
      {/* Chart */}
      <DashboardBarChart
        items={categories.map((name, i) => ({ name, count: data[i] }))}
        prefix="₨"
      />
    </div>
  );
};

const WashDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [districts, setDistricts] = useState([]);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [dashboardCounts, setDashboardCounts] = useState({});
  const [toiletFunctionalData, setToiletFunctionalData] = useState({});
  const [waterStatusData, setWaterStatusData] = useState({}); 
  const [waterUsageData, setWaterUsageData] = useState({}); 
  const [waterSourceData, setWaterSourceData] = useState({});
  const [floodImpactData, setFloodImpactData] = useState({});
  const [handWashingData, setHandWashingData] = useState({});
  const [districtWaterData, setDistrictWaterData] = useState([]);
  const [waterExpenditureData, setWaterExpenditureData] = useState([]);
  const [washCounts, setWashCounts] = useState({ surveyed: 0, approved: 0, rejected: 0, }); 
  const [householdsServedData, setHouseholdsServedData] = useState({ water: 0, sanitation: 0, }); 
  const [districtWashSummary, setDistrictWashSummary] = useState([]); 
  const [ownershipSummary, setOwnershipSummary] = useState([]);
  const [filters, setFilters] = useState({
    district_id: "",
    tehsil_id: "",
    uc_id: "",
    deh_id: "",
    village_id: "",
    dateField: "",
    condition: "",
    dateValue: "",
    startDate: "",
    endDate: "",
  });

  const dateFields = [
    "date_of_selection",
    "date_of_interview",
    "evaluation_date",
    "effective_date",
    "expiry_date",
    "grant_agreement_signing_date",
    "field_verification_date",
    "completion_date",
    "created_at",
  ];

  const conditions = [
    { label: "Less Than", value: "<" },
    { label: "Greater Than", value: ">" },
    { label: "Equal To", value: "=" },
    { label: "Between", value: "between" },
  ];

  const [loading, setLoading] = useState(true);

  // Fetch districts
  useEffect(() => {
    fetchDistricts();
  }, []);

  // When filters change, refetch all WASH data
  useEffect(() => {
    fetchWashData();
  }, [filters]);

  const fetchDistricts = async () => {
    try {
      const response = await fetchData(
        "districts",
        "GET",
        null,
        {},
        {},
        navigate,
        "/districts",
        dispatch
      );
      setDistricts(response?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
      setDistricts([]);
    }
  };

  const fetchWashData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboardCounts(),
        fetchToiletFunctionalData(),
        fetchWaterStatusData(), 
        fetchWaterUsageData(),
        fetchWaterSourceData(),
        fetchFloodImpactData(),
        fetchHandWashingData(),
        fetchWaterExpenditureData(),
        fetchWashCounts(), 
        fetchHouseholdsServed(), 
        fetchDistrictWashSummary(), 
        fetchOwnershipSummary(), 
      ]);
    } catch (error) {
      console.error("Error fetching WASH data:", error);
    }
    setLoading(false);
  };

  const fetchWaterExpenditureData = async () => {
    try {
      const response = await fetchData(
        "water-expenditure-district-wise",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setWaterExpenditureData(response?.data || []);
    } catch (error) {
      console.error("Error fetching water expenditure data:", error);
      setWaterExpenditureData([]);
    }
  };

  const fetchDashboardCounts = async () => {
    try {
      const response = await fetchData(
        "hand-washing-count",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setDashboardCounts(response?.data || {});
    } catch (error) {
      console.error("Error fetching dashboard counts:", error);
      setDashboardCounts({});
    }
  };

  const fetchToiletFunctionalData = async () => {
    try {
      const response = await fetchData(
        "toilet-funcational-count",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setToiletFunctionalData(response?.data || {});
    } catch (error) {
      console.error("Error fetching toilet functional data:", error);
      setToiletFunctionalData({});
    }
  };

  const fetchWaterStatusData = async () => {
    try {
      const response = await fetchData(
        "water-status-count",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setWaterStatusData(response?.data || {});
    } catch (error) {
      console.error("Error fetching water status data:", error);
      setWaterStatusData({});
    }
  };

  const fetchWaterUsageData = async () => {
    try {
      const response = await fetchData(
        "water-usage-hours-count",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setWaterUsageData(response?.data || {});
    } catch (error) {
      console.error("Error fetching water usage data:", error);
      setWaterUsageData({});
    }
  };

  const fetchWaterSourceData = async () => {
    try {
      const response = await fetchData(
        "waterSource-type-count",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setWaterSourceData(response?.data || {});
    } catch (error) {
      console.error("Error fetching water source data:", error);
      setWaterSourceData({});
    }
  };

  const fetchFloodImpactData = async () => {
    try {
      const response = await fetchData(
        "flood-impact-count",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setFloodImpactData(response?.data || {});
    } catch (error) {
      console.error("Error fetching flood impact data:", error);
      setFloodImpactData({});
    }
  };

  const fetchHandWashingData = async () => {
    try {
      const response = await fetchData(
        "hand-washing-count",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setHandWashingData(response?.data || {});
    } catch (error) {
      console.error("Error fetching hand washing data:", error);
      setHandWashingData({});
    }
  };


  const fetchWashCounts = async () => {
    try {
      const response = await fetchData(
        "wash-approved-reject-count",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setWashCounts(
        response?.data || { surveyed: 0, approved: 0, rejected: 0 }
      );
    } catch (error) {
      console.error("Error fetching wash counts:", error);
      setWashCounts({ surveyed: 0, approved: 0, rejected: 0 });
    }
  };

  const fetchHouseholdsServed = async () => {
    try {
      const response = await fetchData(
        "wash-households-served", 
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setHouseholdsServedData(response?.data || { water: 0, sanitation: 0 });
    } catch (error) {
      console.error("Error fetching households served:", error);
      setHouseholdsServedData({ water: 0, sanitation: 0 });
    }
  };

  const fetchDistrictWashSummary = async () => {
    try {
      const response = await fetchData(
        "district-wise-wash-summary",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setDistrictWashSummary(response?.data || []);
    } catch (error) {
      console.error("Error fetching district wash summary:", error);
      setDistrictWashSummary([]);
    }
  };

  const fetchOwnershipSummary = async () => {
    try {
      const response = await fetchData(
        "district-wise-ownership-summary",
        "GET",
        null,
        {},
        { ...filters },
        navigate,
        "/wash-dashboard",
        dispatch
      );
      setOwnershipSummary(response?.data || []);
    } catch (error) {
      console.error("Error fetching ownership summary:", error);
      setOwnershipSummary([]);
    }
  };

  // Format water source data for pie chart
  const waterSourceChartData = useMemo(() => {
    if (!waterSourceData) return [];
    return [
      { name: "Tap Water", value: waterSourceData.TapWater || 0, color: "#0088FE", },
      { name: "Hand Pump", value: waterSourceData.HandPump || 0, color: "#00C49F", },
      { name: "Dug Well", value: waterSourceData.DugWell || 0, color: "#FFBB28", },
      { name: "Rain Water", value: waterSourceData.RainWater || 0, color: "#FF8042", },
      { name: "Surface Water", value: waterSourceData.SurfaceWater || 0, color: "#8884D8", },
    ].filter((item) => item.value > 0);
  }, [waterSourceData]);

  // Format toilet functional data for bar chart
  const toiletFunctionalChartData = useMemo(() => {
    return [
      { status: "Fully Functional", count: toiletFunctionalData.fullyFunctional || 0, color: BRAND.teal, },
      { status: "Partly Functional", count: toiletFunctionalData.partlyFunctional || 0, color: BRAND.gold, },
      { status: "Non-Functional", count: toiletFunctionalData.nonFunctional || 0, color: getPaletteColor(4), },
    ];
  }, [toiletFunctionalData]);

  // Format flood impact data for pie chart
  const floodImpactChartData = useMemo(() => {
    return [
      { name: "No Damage", value: floodImpactData.noDamage || 0, color: "#10b981", },
      { name: "Repaired", value: floodImpactData.repair || 0, color: "#3b82f6", },
      { name: "Not Repaired", value: floodImpactData.noRepair || 0, color: "#f59e0b", },
      { name: "Minor Damage", value: floodImpactData.minorDamage || 0, color: "#ef4444", },
    ].filter((item) => item.value > 0);
  }, [floodImpactData]);

  // Format water expenditure data for chart
  const formattedExpenditureData = useMemo(() => {
    if (!waterExpenditureData || waterExpenditureData.length === 0) {
      return { categories: [], data: [] };
    }
    const categories = waterExpenditureData.map(
      (item) => item.district_name || "Unknown"
    );
    const data = waterExpenditureData.map(
      (item) => parseInt(item.total_yearly_expenditure) || 0
    );
    return { categories, data };
  }, [waterExpenditureData]);

  const handleFilterChange = (key, value) => {
    if (typeof key === "object") {
      setFilters(key);
    } else {
      // Received single key/value
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  const applyFilters = () => {
    // fetchWashData will run automatically because filters state changed,
    // but if you want to force immediate fetch you can call fetchWashData() here.
    fetchWashData();
  };

  // Handle Reset button click (if you want a local reset)
  const resetFilters = () => {
    const reset = {
      district_id: "",
      tehsil_id: "",
      uc_id: "",
      deh_id: "",
      village_id: "",
      dateField: "",
      condition: "",
      dateValue: "",
      startDate: "",
      endDate: "",
    };
    setFilters(reset);
    // fetchWashData will run due to filters change via useEffect
  };

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };


  // Compute pending count
  const pendingCount = Math.max(0, washCounts.surveyed - washCounts.approved - washCounts.rejected);

  const has = {
    surveyed: hasData(washCounts.surveyed),
    served: hasData(householdsServedData.water, householdsServedData.sanitation),
    sources: hasData(waterSourceData),
    usage: hasData(waterUsageData),
    waterStatus: hasData(waterStatusData),
    toilets: hasData(toiletFunctionalChartData.map((item) => item.count)),
    ownership: hasData(
      ownershipSummary.map((d) => [
        d.water_communal,
        d.water_private,
        d.sanitation_communal,
        d.sanitation_private,
      ])
    ),
    expenditure: hasData(formattedExpenditureData.data),
    flood: hasData(floodImpactData),
    summary: districtWashSummary.length > 0,
  };

  const anyData = Object.values(has).some(Boolean);
  const showRow = () => loading || anyData;
  const panel = (hasPanelData, body) =>
    !loading && !hasPanelData ? <NoData /> : body;

  return (
    <div className="w-full px-4 bg-gray-50">
      {/* Filters */}
      <DashboardSearchFilters
        accordionTitle="WASH Filters"
        defaultExpanded={false}
        fetchOnChange={true}
        apiRoutes={{
          districts: "districts",
          tehsils: "tehsils",
          ucs: "ucs",
          dehs: "dehs",
          villages: "villages",
        }}
        dateFields={["date_time_of_survey"]}
        onApply={handleFilterChange}
      />
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
          <p className="mt-2 text-gray-600">Loading WASH data...</p>
        </div>
      )}
      {/* Main Content */}
      <div className="bg-white border-dashed border-2 border-teal-700 min-h-40 w-full px-5 rounded-lg shadow-sm">
        <div className="space-y-4">
          {!loading && !Object.values(has).some(Boolean) && (
            <NoData className="py-16" />
          )}
          {/* Top Row - WASH Survey Status, Households Served, Water Sources */}
          {showRow(has.surveyed, has.served, has.sources) && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4">
            {/* WASH Survey Status Cards */}
            <div className="xl:col-span-4">
              <div className="text-center mb-3">
                <h3 className="text-xl my-2 text-[#3A3A3A] font-bold uppercase text-balance"> WASH Survey Status ({formatCount(washCounts.surveyed)}) </h3>
                {panel(has.surveyed,
                <div className="grid grid-cols-3 gap-2">
                  <ActivityTypeCard title="Approved" count={washCounts.approved} variant="approved" />
                  <ActivityTypeCard title="Pending" count={pendingCount} variant="pending" />
                  <ActivityTypeCard title="Rejected" count={washCounts.rejected} variant="rejected" />
                </div>)}
              </div>
            </div>
            {/* Households Served Donut */}
            <div className="xl:col-span-4">
              <div className="text-center mb-3">
                <h3 className="text-xl my-2 text-[#3A3A3A] font-bold uppercase text-balance"> Households Served </h3>
                {panel(has.served,
                <div className="mt-3">
                  <HouseholdsServedDonut waterServed={householdsServedData.water} sanitationServed={householdsServedData.sanitation} />
                </div>)}
              </div>
            </div>
            {/* Water Sources Donut */}
            <div className="xl:col-span-4">
              <div className="bg-white flex flex-col rounded-lg">
                <h3 className="text-xl my-2 text-[#3A3A3A] text-center font-bold uppercase text-balance"> Water Sources </h3>
                {panel(has.sources,
                <div className="flex items-center justify-center">
                  <DonutChart data={[
                    { name: "Tap Water", value: waterSourceData.TapWater || 0, },
                    { name: "Hand Pump", value: waterSourceData.HandPump || 0, },
                    { name: "Protected Dug Well", value: waterSourceData.DugWell || 0, },
                    { name: "Unprotected Dug Well", value: waterSourceData.UnprotectedDugWell || 0, },
                    { name: "Rain Water", value: waterSourceData.RainWater || 0, },
                    { name: "Surface Water", value: waterSourceData.SurfaceWater || 0, },
                  ]} centerValue={formatNumber(
                    (waterSourceData.TapWater || 0) + (waterSourceData.HandPump || 0) + (waterSourceData.DugWell || 0) + (waterSourceData.UnprotectedDugWell || 0) + (waterSourceData.RainWater || 0) + (waterSourceData.SurfaceWater || 0)
                  )} centerLabel="Total" />
                </div>)}
              </div>
            </div>
          </div>
          )}
          {/* Second Row - Water Usage + Water Status + Toilet Status */}
          {showRow(has.usage, has.waterStatus, has.toilets) && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4">
            <div className="xl:col-span-4">
              <WaterUsageHorizontalBar data={waterUsageData} />
            </div>
            <div className="xl:col-span-4">
              <WaterStatusBar data={waterStatusData} />
            </div>
            {/* Toilet Functional Status */}
            <div className="xl:col-span-4">
              <div className="text-center">
                <h3 className="text-xl mb-2 p-0 text-[#3A3A3A] font-bold uppercase text-balance"> Toilet Functional Status </h3>
                <div className="mt-4 flex justify-center items-center">
                  <div className="grid grid-cols-3 gap-5 w-full max-w-md">
                    {toiletFunctionalChartData.map((item, index) => {
                      const total = toiletFunctionalChartData.reduce(
                        (sum, d) => sum + d.count,
                        0
                      );
                      const percentage = total > 0 ? (item.count / total) * 100 : 0;
                      const radius = 38;
                      const circumference = 2 * Math.PI * radius;
                      const strokeDashoffset = circumference - (percentage / 100) * circumference;
                      return (
                        <div key={index} className="flex flex-col items-center">
                          <div className="relative w-28 h-28">
                            <svg className="transform -rotate-90 w-28 h-28">
                              <circle cx="56" cy="56" r={radius} stroke={NEUTRAL.border} strokeWidth="9" fill="transparent" />
                              <circle cx="56" cy="56" r={radius} stroke={item.color} strokeWidth="9" fill="transparent" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" className="transition-all duration-500" />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-2xl font-bold" style={{ color: item.color }} > {item.count} </span>
                            </div>
                          </div>
                          <div className="mt-2.5 text-center">
                            <p className="text-xs font-semibold text-gray-700"> {item.status} </p>
                            <p className="text-xs text-gray-500"> {percentage.toFixed(1)}% </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
          </>)}
        </div>
        {/* Row - District-wise Ownership Summary with Flood Impact */}
        {showRow(has.ownership, has.expenditure, has.flood) && (<>
        <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
        {/* Row - District-wise Ownership Summary with Flood Impact */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-4">
            <div className="bg-white rounded-lg flex flex-col h-full">
              <h3 className="text-xl text-[#3A3A3A] font-bold uppercase text-center text-balance"> District-wise Ownership </h3>
              <div className="flex-1 flex justify-center items-center">
                <div className="w-full">
                  {panel(has.ownership,
                  <DashboardBarChart
                    categories={ownershipSummary.map((d) => d.district_name || "N/A")}
                    series={[
                      { name: "Water Communal", data: ownershipSummary.map((d) => d.water_communal) },
                      { name: "Water Private", data: ownershipSummary.map((d) => d.water_private) },
                      { name: "Sanitation Communal", data: ownershipSummary.map((d) => d.sanitation_communal) },
                      { name: "Sanitation Private", data: ownershipSummary.map((d) => d.sanitation_private) },
                    ]}
                  />)}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="bg-white rounded-lg flex flex-col h-full">
              <h3 className="text-xl text-[#3A3A3A] font-bold uppercase text-center text-balance"> Total Yearly Expenditure </h3>
              <div className="flex-1 mt-5 flex justify-center items-center">
                <TotalYearlyExpenditureDistrictWiseChart expenditureData={formattedExpenditureData} />
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 bg-white flex flex-col rounded-lg h-full">
            <h3 className="text-lg font-bold text-center text-[#3A3A3A] uppercase text-balance"> Flood Impact Status </h3>
            <div className="flex mt-4 items-center justify-center">
              <FloodImpactPie data={floodImpactData} />
            </div>
          </div>
        </div>
        </>)}
        {/* Last Row - Full Width Table: District-wise WASH Survey Summary */}
        {showRow(has.summary) && (<>
        <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-12">
            <h3 className="text-xl font-bold uppercase text-[#3A3A3A] mb-3 text-center text-balance"> District-wise WASH Survey Summary </h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-white bg-[#137e81] font-medium"> District </th>
                    <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium"> No. of Handpumps </th>
                    <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium"> No. of Latrines </th>
                    <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium"> Total </th>
                  </tr>
                </thead>
                <tbody>
                  {districtWashSummary.length > 0 ? (
                    districtWashSummary.map((district, index) => {
                      // Calculate total as sum of water + sanitation
                      const water = parseInt(district.water) || 0;
                      const sanitation = parseInt(district.sanitation) || 0;
                      const total = water + sanitation;
                      return (
                        <tr key={index} className={`${ index % 2 === 0 ? "bg-[#F6F7F9]" : "bg-white" } hover:bg-gray-100`} >
                          <td className="px-4 py-3 text-gray-800 border-b font-medium"> {district.district_name || "N/A"} </td>
                          <td className="px-4 py-3 text-center border-b font-semibold"> {formatCount(water)} </td>
                          <td className="px-4 py-3 text-center border-b font-semibold"> {formatCount(sanitation)} </td>
                          <td className="px-4 py-3 text-center border-b font-bold text-[#137e81]"> {formatCount(total)} </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500" > {loading ? "Loading data..." : "No data available"} </td>
                    </tr>
                  )}
                  {/* Grand Total Row */}
                  {districtWashSummary.length > 0 && (
                    <tr className="bg-[#137e81] text-white font-bold">
                      <td className="px-4 py-3 text-left border-b">TOTAL</td>
                      <td className="px-4 py-3 text-center border-b"> {formatCount( districtWashSummary.reduce( (sum, d) => sum + (parseInt(d.water) || 0), 0 ) )} </td>
                      <td className="px-4 py-3 text-center border-b"> {formatCount( districtWashSummary.reduce( (sum, d) => sum + (parseInt(d.sanitation) || 0), 0 ) )} </td>
                      <td className="px-4 py-3 text-center border-b"> {formatCount( districtWashSummary.reduce((sum, d) => { const water = parseInt(d.water) || 0; const sanitation = parseInt(d.sanitation) || 0; return sum + water + sanitation; }, 0) )} </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
};

export default WashDashboard;